import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { UserCheck, ArrowRight, Sparkles } from 'lucide-react';
import axios from 'axios';
import { useUserAuth } from '../contexts/UserAuthContext';
import { resolveImageUrl } from './ImageUpload';
import { linkHoverProps } from '../lib/nativeCache';
import StatusBadge from './StatusBadge';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const formatDate = (iso) => iso ? new Date(iso).toLocaleDateString('en-US', { month: 'short', day: 'numeric' }) : '';

/**
 * PersonalRows — Following + "Because You Follow…" rows shown only when
 * the user is signed in AND has activity. Renders nothing for guests or
 * empty states so the homepage stays clean.
 * iter239 · Continue Watching row removed per Jay (no watch content yet
 * — bring back later once video watch history matters).
 */
const PersonalRows = ({ compact = false }) => {
  const { user } = useUserAuth();
  const [following, setFollowing] = useState([]);
  const [byf, setByf] = useState([]); // because-you-follow rows

  useEffect(() => {
    if (!user) return;
    axios.get(`${API}/me/following`, { withCredentials: true })
      .then(r => setFollowing((r.data.athletes || []).slice(0, 6)))
      .catch(() => setFollowing([]));
    axios.get(`${API}/me/because-you-follow`, { withCredentials: true })
      .then(r => setByf(r.data.rows || []))
      .catch(() => setByf([]));
  }, [user]);

  if (!user) return null;
  if (following.length === 0 && byf.length === 0) return null;

  return (
    <section className="py-12 bg-[#f7f6f3] border-y border-gray-200" data-testid="personal-rows">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 space-y-12">
        {/* Because You Follow… — single compact row of stories from all followed athletes */}
        {byf.length > 0 && (() => {
          // Flatten one story per followed athlete (or up to 2 per athlete) → max 8 cards.
          const cards = byf.flatMap((row) => (row.stories || []).slice(0, 2).map((s) => ({ s, a: row.athlete }))).slice(0, 8);
          if (cards.length === 0) return null;
          return (
            <div data-testid="byf-row">
              <div className="flex items-center justify-between mb-5">
                <div>
                  <p className="font-italic text-xs uppercase tracking-widest text-blue-500 mb-1 inline-flex items-center gap-1.5">
                    <Sparkles className="w-3.5 h-3.5" /> Because you follow
                  </p>
                  <h2 className="font-display text-2xl sm:text-3xl uppercase">Picked for you</h2>
                </div>
                <Link to="/" className="text-sm font-bold hover-gradient">All →</Link>
              </div>
              <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-4">
                {cards.map(({ s, a }) => (
                  <Link key={`${a.slug}-${s.id}`} to={`/stories/${s.slug}`} className="group block bg-white rounded-2xl overflow-hidden ring-1 ring-black/5 hover:ring-blue-400/60 transition">
                    <div className="aspect-video bg-gray-100 relative">
                      {s.thumbnail && <img src={resolveImageUrl(s.thumbnail, 400)} alt={s.name} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />}
                      <div className="absolute top-2 left-2"><StatusBadge story={s} /></div>
                    </div>
                    <div className="p-3">
                      <div className="flex items-center gap-1.5 mb-1.5">
                        {a.photo && <img loading="lazy" decoding="async" src={resolveImageUrl(a.photo, 64)} alt="" className="w-4 h-4 rounded-full object-cover" />}
                        <span className="text-[10px] uppercase tracking-widest text-blue-600 font-bold truncate">{a.name}</span>
                      </div>
                      <p className="font-display text-sm uppercase line-clamp-2">{s.name}</p>
                      <p className="text-[10px] uppercase tracking-widest text-gray-500 mt-1">{s.category} · {formatDate(s.publish_date)}</p>
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          );
        })()}

        {following.length > 0 && !compact && (
          <div data-testid="following-row">
            <div className="flex items-center justify-between mb-5">
              <div>
                <p className="font-italic text-xs uppercase tracking-widest text-blue-500 mb-1">Your World</p>
                <h2 className="font-display text-2xl sm:text-3xl uppercase flex items-center gap-2">
                  <UserCheck className="w-5 h-5" /> Following
                </h2>
              </div>
              <Link to="/profile?tab=following" className="text-sm font-bold hover-gradient">All →</Link>
            </div>
            <div className="flex gap-3 overflow-x-auto no-scrollbar">
              {following.map(a => (
                <Link key={a.id} to={`/network/${a.slug}`} className="flex-shrink-0 w-32 text-center" {...linkHoverProps(a.slug)}>
                  <div className="w-32 h-32 rounded-2xl overflow-hidden bg-gray-100 ring-1 ring-black/5">
                    {a.photo && <img loading="lazy" decoding="async" src={resolveImageUrl(a.photo, 200)} alt="" className="w-full h-full object-cover" />}
                  </div>
                  <p className="font-display text-xs uppercase mt-2 truncate">{a.name}</p>
                  <p className="text-[9px] uppercase tracking-widest text-gray-500 mt-0.5 truncate">{a.sport}</p>
                </Link>
              ))}
              <Link to="/" className="flex-shrink-0 w-32 flex items-center justify-center text-xs font-bold text-blue-500 hover:text-black">
                Discover more <ArrowRight className="w-3 h-3 ml-1" />
              </Link>
            </div>
          </div>
        )}
      </div>
    </section>
  );
};

export default PersonalRows;
